import firebase from '@/firebase';
import 'firebase/auth';
import 'firebase/firestore';
import { BehaviorSubject, from } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import * as R from 'ramda';
import Task from '@/services/task';
export var AuthRole;
(function (AuthRole) {
    AuthRole["anonymous"] = "anonymous";
    AuthRole["other"] = "other";
    AuthRole["staff"] = "staff";
})(AuthRole || (AuthRole = {}));
export class Session {
    constructor(role, token = '', userId = null) {
        this.role = role;
        this.token = token;
        this.userId = userId;
    }
    get isValid() {
        return this.role == AuthRole.staff;
    }
    equals(session) {
        return this.role === session.role
            && this.token === session.token
            && this.userId === session.userId;
    }
}
export default new class AuthService {
    constructor() {
        this._session = new BehaviorSubject(null);
        this._user = new BehaviorSubject(null);
        this.listenOnSession();
    }
    get session() {
        return this._session.asObservable().pipe(filter(it => it != null), map(it => it), distinctUntilChanged((a, b) => a.equals(b)));
    }
    get validSession() {
        return this.session.pipe(filter(it => it.isValid));
    }
    get isValidSession() {
        return this.session.pipe(map(session => session.role != AuthRole.anonymous), distinctUntilChanged());
    }
    get currentSession() {
        return this._session.value;
    }
    get currentSessionIsValid() {
        return this.currentSession != null && this.currentSession.isValid;
    }
    get user() {
        return this._user.asObservable().pipe(filter(it => it != null), map(it => it), distinctUntilChanged());
    }
    get currentUser() {
        return this._user.value;
    }
    login(email, password) {
        const promise = firebase.auth().signInWithEmailAndPassword(email, password);
        return from(promise).pipe(map(() => Task.success(null)), startWith(Task.loading()), catchError(error => [Task.failure(error)]));
    }
    register(email, password) {
        const promise = firebase.auth().createUserWithEmailAndPassword(email, password);
        return from(promise).pipe(map(() => Task.success(null)), startWith(Task.loading()), catchError(error => [Task.failure(error)]));
    }
    sendPasswordResetEmail(email) {
        const url = `${process.env.VUE_APP_BASE_URL}/reset-password`;
        console.log(`Reset password url: ${url}`);
        const promise = firebase.auth().sendPasswordResetEmail(email, {
            url: url
        });
        return from(promise).pipe(map(() => Task.success(null)), startWith(Task.loading()), catchError(error => [Task.failure(error)]));
    }
    logout() {
        const promise = from(firebase.auth().signOut());
        return from(promise).pipe(map(() => Task.success(null)), startWith(Task.loading()), catchError(error => [Task.failure(error)]));
    }
    setSession(session) {
        console.log(`[auth] role: ${session.role}`);
        if (session.isValid && this.firestoreUnsubscribe != null) {
            this.firestoreUnsubscribe();
        }
        this._session.next(session);
    }
    listenOnSession() {
        firebase.auth().onAuthStateChanged(async (user) => {
            if (this._user.value != null && user != null && this._user.value.uid == user.uid) {
                return;
            }
            console.log(`[auth] user: ${user != null}`);
            this._user.next(user);
            if (this.firestoreUnsubscribe != null) {
                this.firestoreUnsubscribe();
            }
            if (user == null) {
                this.setSession(new Session(AuthRole.anonymous));
                return;
            }
            const valid = await this.refreshSession(false);
            if (!valid) {
                this.firestoreUnsubscribe = firebase.firestore()
                    .collection('metadata')
                    .doc(this._user.value.uid)
                    .onSnapshot(async () => {
                    await this.refreshSession(true);
                });
            }
        });
    }
    async refreshSession(force) {
        if (this._user.value == null) {
            return false;
        }
        const result = await this._user.value.getIdTokenResult(force);
        const role = R.path(['claims', 'https://hasura.io/jwt/claims', 'x-hasura-default-role'], result);
        const userId = R.path(['claims', 'https://hasura.io/jwt/claims', 'x-hasura-user-id'], result);
        if (role != undefined && userId != undefined) {
            let authRole;
            if (role === 'staff') {
                authRole = AuthRole.staff;
            }
            else {
                authRole = AuthRole.other;
            }
            this.setSession(new Session(authRole, result.token, userId));
            return true;
        }
        else {
            this.setSession(new Session(AuthRole.anonymous));
            return false;
        }
    }
};
// export default authService
